// KIO-01: configuración y cuenta regresiva de la pantalla de espera de pago.
//
// Los campos viven en config/styles (style.global.checkout) y los edita kiosk-dashboard, que se
// publica por separado. Por eso cada valor tiene un default y se valida aquí: un campo que falta,
// viene con otro tipo o con un valor que esta versión no conoce cae al default en vez de romper la
// pantalla de cobro. Así da lo mismo cuál de los dos proyectos se publique primero.

export const POS_SIDES = ['right', 'left', 'bottom', 'bottom-right', 'bottom-left'];

export const PAYMENT_WAIT_DEFAULTS = Object.freeze({
  posSide: 'right',
  paymentTimeoutSeconds: 90,
  paymentSound: true,
  paymentSoundVolume: 1,
});

// KIO-03 fija 120 s como máximo duro de la espera. El mínimo es 10 s porque hay terminales Getnet
// que cortan la venta en menos de 30 s; la cuenta regresiva no debe durar más que el POS.
export const PAYMENT_TIMEOUT_MIN_SECONDS = 10;
export const PAYMENT_TIMEOUT_MAX_SECONDS = 120;

// Segundos restantes en que suena el segundo aviso.
export const PAYMENT_WARNING_SECONDS = 15;

// Cada cuánto se actualiza el texto para lectores de pantalla.
export const PAYMENT_ANNOUNCE_STEP_SECONDS = 10;

const toNumber = (value) => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value.trim() !== '') return Number(value);
  return NaN;
};

const toBoolean = (value, fallback) => {
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return fallback;
};

/**
 * Lee la configuración de la pantalla de espera desde el documento de estilos, con defaults.
 *
 * @param {object} style - Documento config/styles del cliente (puede venir vacío o incompleto).
 * @returns {{posSide: string, paymentTimeoutSeconds: number, paymentSound: boolean, paymentSoundVolume: number}}
 */
export function getPaymentWaitConfig(style) {
  const checkout = style?.global?.checkout || {};

  const posSide = POS_SIDES.includes(checkout.posSide) ? checkout.posSide : PAYMENT_WAIT_DEFAULTS.posSide;

  const timeout = toNumber(checkout.paymentTimeoutSeconds);
  const paymentTimeoutSeconds = Number.isFinite(timeout)
    ? Math.min(PAYMENT_TIMEOUT_MAX_SECONDS, Math.max(PAYMENT_TIMEOUT_MIN_SECONDS, Math.round(timeout)))
    : PAYMENT_WAIT_DEFAULTS.paymentTimeoutSeconds;

  const paymentSound = toBoolean(checkout.paymentSound, PAYMENT_WAIT_DEFAULTS.paymentSound);

  const volume = toNumber(checkout.paymentSoundVolume);
  const paymentSoundVolume = Number.isFinite(volume)
    ? Math.min(1, Math.max(0, volume))
    : PAYMENT_WAIT_DEFAULTS.paymentSoundVolume;

  return { posSide, paymentTimeoutSeconds, paymentSound, paymentSoundVolume };
}

/**
 * Segundos que quedan, redondeados hacia arriba: con 89,2 s restantes se muestra 90, y el 0 solo
 * aparece cuando de verdad se acabó el tiempo.
 */
export function getRemainingSeconds(totalSeconds, elapsedMs) {
  const remaining = Math.ceil((totalSeconds * 1000 - elapsedMs) / 1000);
  return Math.min(totalSeconds, Math.max(0, remaining));
}

/**
 * Valor que se anuncia al lector de pantalla: el múltiplo de 10 hacia arriba, para que el texto
 * cambie cada 10 s y no cada segundo.
 */
export function getAnnouncedSeconds(remainingSeconds, step = PAYMENT_ANNOUNCE_STEP_SECONDS) {
  if (remainingSeconds <= 0) return 0;
  return Math.ceil(remainingSeconds / step) * step;
}

/**
 * Cuenta regresiva basada en la hora de inicio, no en contar ticks: si el navegador atrasa un
 * intervalo (pestaña ocupada, equipo lento), el número sigue siendo el correcto.
 *
 * Es solo visual. Al llegar a 0 avisa con onTimeout, pero no corta el cobro: eso lo decide KIO-03.
 *
 * @param {object} options
 * @param {number} options.totalSeconds
 * @param {(remainingSeconds: number) => void} [options.onTick] - En cada cambio del número.
 * @param {() => void} [options.onWarning] - Una vez, al quedar PAYMENT_WARNING_SECONDS o menos.
 * @param {() => void} [options.onTimeout] - Una vez, al llegar a 0.
 * @param {() => number} [options.now] - Reloj (para pruebas).
 * @returns {{start: () => void, stop: () => void}}
 */
export function createCountdown({
  totalSeconds,
  onTick,
  onWarning,
  onTimeout,
  warningSeconds = PAYMENT_WARNING_SECONDS,
  tickMs = 250,
  now = () => Date.now(),
}) {
  let startedAt = null;
  let intervalId = null;
  let lastRemaining = null;
  // Si la espera total ya es menor que el umbral, el aviso de "queda poco" no aporta nada.
  let warned = totalSeconds <= warningSeconds;
  let timedOut = false;

  const stop = () => {
    if (intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
  };

  const tick = () => {
    const remaining = getRemainingSeconds(totalSeconds, now() - startedAt);
    if (remaining !== lastRemaining) {
      lastRemaining = remaining;
      onTick?.(remaining);
    }
    if (!warned && remaining <= warningSeconds) {
      warned = true;
      onWarning?.();
    }
    if (!timedOut && remaining <= 0) {
      timedOut = true;
      stop();
      onTimeout?.();
    }
  };

  const start = () => {
    stop();
    startedAt = now();
    tick();
    if (!timedOut) intervalId = setInterval(tick, tickMs);
  };

  return { start, stop };
}
